import { useMemo } from 'react';
import { useUserOptionRender, usePatientOptionRender } from '@src/hooks';
import { APPOINTMENT_STATUS_OPTIONS, SERVICE_TYPE_OPTIONS } from '../constants';
import { cleanPhoneNumber, getPhoneFieldConfig } from '@src/utils/phoneUtils';
import { formatPersonNameField } from '@src/utils/nameUtils';
import dayjs from 'dayjs';
import { useOne } from '@refinedev/core';
import { useOfferingsInsights } from '@src/pages/followups/hooks';
import {
  appointmentScheduleTimeProps,
  clampToAppointmentScheduleHours,
  getDefaultAppointmentScheduleTime,
} from '@src/utils/appointmentScheduleTime';

/** Opens the patient profile in a new browser tab (same origin). */
export function openPatientProfileInNewTab(
  patientId: string | number | null | undefined
) {
  if (patientId === null || patientId === undefined || patientId === '') return;
  const id = String(patientId).trim();
  if (!id || id === '0') return;
  const url = `${window.location.origin}/patients/${id}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export const useAppointmentsFormFields = (
  initialPatientId?: string | number,
  record?: any // Add record parameter for edit mode
) => {
  const userOptionRender = useUserOptionRender({
    allowedUserTypes: ['doctor', 'therapist', 'counsellor'],
    showUserType: true,
    size: 'small',
  });

  const patientOptionRender = usePatientOptionRender({
    showMobile: true,
    showStatus: true,
    size: 'small',
  });

  // Fetch patient data if initialPatientId is provided
  const { data: patientData } = useOne({
    resource: 'patients',
    id: initialPatientId?.toString() || '',
    queryOptions: {
      enabled: !!initialPatientId,
    },
  });

  const patient = patientData?.data;

  return useMemo(
    () => [
      // Row 1 - Patient Search Selector
      {
        name: 'patient_id',
        label: 'Patient',
        type: 'resourceSelect' as const,
        required: false,
        placeholder: 'Search and select patient (optional)...',
        layout: { span: 8 },
        defaultValue: initialPatientId,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          loading: false,
          allowClear: true,
        },
        searchSelectProps: {
          resource: 'patients',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name', 'mobile_no'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: patientOptionRender,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
        onChange: (value: any, form: any) => {
          if (value?.data) {
            const patient = value.data;

            const firstName = formatPersonNameField(patient.first_name || '');
            const lastName = formatPersonNameField(patient.last_name || '');
            const fullName = [firstName, lastName].filter(Boolean).join(' ');
            const mobileNo = cleanPhoneNumber(patient.mobile_no || '');

            if (form && typeof form.setFieldsValue === 'function') {
              // Use setFieldsValue for better compatibility
              form.setFieldsValue({
                patient_name: fullName,
                patient_mobile_no: mobileNo,
              });

              // Also try the legacy method as fallback
              setTimeout(() => {
                if (typeof form.setFields === 'function') {
                  form.setFields([
                    { name: 'patient_name', value: fullName },
                    { name: 'patient_mobile_no', value: mobileNo },
                  ]);
                }
              }, 50);

              // Trigger validation for the updated fields
              if (typeof form.validateFields === 'function') {
                form
                  .validateFields(['patient_name', 'patient_mobile_no'])
                  .catch(() => {});
              }
            }
          } else {
            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                patient_name: '',
                patient_mobile_no: '',
              });

              // Also try the legacy method as fallback
              setTimeout(() => {
                if (typeof form.setFields === 'function') {
                  form.setFields([
                    { name: 'patient_name', value: '' },
                    { name: 'patient_mobile_no', value: '' },
                  ]);
                }
              }, 50);

              if (typeof form.validateFields === 'function') {
                form
                  .validateFields(['patient_name', 'patient_mobile_no'])
                  .catch(() => {});
              }
            }
          }
        },
        tooltip: 'Search by patient name or mobile number',
      },

      {
        name: 'patient_name',
        label: 'Patient Name',
        type: 'text' as const,
        required: true,
        placeholder: 'Enter patient name or auto-filled from selection...',
        layout: { span: 8 },
        defaultValue: patient
          ? [formatPersonNameField(patient.first_name || ''), formatPersonNameField(patient.last_name || '')]
              .filter(Boolean)
              .join(' ')
          : undefined,
        props: {
          size: 'large',
        },
        tooltip:
          'Enter patient name manually for new patients, or auto-filled for existing patients',
        transform: {
          output: (value: any) => formatPersonNameField(value),
        },
      },
      {
        name: 'patient_mobile_no',
        label: 'Patient Mobile',
        ...getPhoneFieldConfig(true, '98765 43210'),
        layout: { span: 8 },
        props: {
          ...getPhoneFieldConfig().props,
          size: 'large',
        },
        tooltip:
          'Enter 10-digit mobile number manually for new patients, or auto-filled for existing patients (without country code)',
      },

      // Row 2 - Appointed To & Service Type
      {
        name: 'appointed_to_user_id',
        label: 'Appointed To',
        type: 'resourceSelect' as const,
        required: true,
        placeholder: 'Who should handle this appointment?',
        layout: { span: 12 },
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          loading: false,
          allowClear: true,
        },
        searchSelectProps: {
          resource: 'users',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: 'active',
            },
            {
              field: 'type',
              operator: 'in',
              value: ['doctor', 'therapist', 'counsellor'],
            },
          ],
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: userOptionRender,
          autoSelectFirst: true,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
        tooltip: 'Assign this appointment to a team member',
      },
      {
        name: 'service_type',
        label: 'Service Type',
        type: 'select' as const,
        required: true,
        placeholder: 'Select service type',
        layout: { span: 12 },
        options: SERVICE_TYPE_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Select the type of service for this appointment',
      },

      // Row 3 - Duration & Scheduled DateTime
      {
        name: 'duration_in_mins',
        label: 'Duration (minutes)',
        type: 'number' as const,
        required: false,
        placeholder: 'Enter duration in minutes',
        layout: { span: 12 },
        props: {
          size: 'large',
          min: 1,
          step: 15,
        },
        tooltip: 'Duration of the appointment in minutes',
      },
      {
        name: 'scheduled_datetime',
        label: 'Scheduled Date & Time',
        type: 'datetime' as const,
        required: true,
        placeholder: 'When is the appointment?',
        layout: { span: 12 },
        defaultValue: getDefaultAppointmentScheduleTime(),
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: true,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          disabledDate: (current: any) => {
            return current && current < dayjs().startOf('day');
          },
          ...appointmentScheduleTimeProps,
          placeholder: 'Select date and time (10 AM – 7 PM)',
        },
        tooltip: 'Set the date and time for the appointment (10 AM – 7 PM)',
        onChange: (value: any, form: any) => {
          if (!value || !form || typeof form.setFieldsValue !== 'function') {
            return;
          }

          const normalizedScheduled = clampToAppointmentScheduleHours(dayjs(value));

          // Auto-set follow-up date to 1 day before at 10:30 AM
          let followUpDate = normalizedScheduled.subtract(1, 'day');

          // If the follow-up date falls on Sunday, move it to Saturday
          if (followUpDate.day() === 0) {
            followUpDate = followUpDate.subtract(1, 'day');
          }

          // Set time to 10:30 AM
          followUpDate = followUpDate
            .hour(10)
            .minute(30)
            .second(0)
            .millisecond(0);

          // Update both fields in a single call to prevent conflicts
          form.setFieldsValue({
            scheduled_datetime: normalizedScheduled,
            follow_up_date: followUpDate,
          });
        },
      },

      // Row 4 - Status & Notes
      {
        name: 'status',
        label: 'Status',
        type: 'select' as const,
        required: true,
        defaultValue: 'PENDING',
        placeholder: "What's the current status?",
        layout: { span: 12 },
        options: APPOINTMENT_STATUS_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Current status of this appointment',
      },
      // Row 5 - Next Appointment Date (conditional based on status=COMPLETED)
      {
        name: 'next_appointment_date',
        label: 'Next Appointment Date & Time',
        type: 'datetime' as const,
        required: false,
        placeholder: 'Select next appointment date and time',
        layout: { span: 12 },
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: true,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          disabledDate: (current: any) => {
            return current && current < dayjs().startOf('day');
          },
          ...appointmentScheduleTimeProps,
          placeholder: 'Select next appointment date and time (10 AM – 7 PM)',
        },
        onChange: (value: any, form: any) => {
          if (!value || !form || typeof form.setFieldsValue !== 'function') {
            return;
          }

          form.setFieldsValue({
            next_appointment_date: clampToAppointmentScheduleHours(dayjs(value)),
          });
        },
        tooltip: 'Schedule the next appointment when current appointment is completed',
        hidden: true, // Hide by default - will be shown conditionally via form logic
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'textarea' as const,
        required: false,
        placeholder: 'Add any important notes for appointment...',
        layout: { span: 12 },
        props: {
          rows: 3,
          showCount: true,
          maxLength: 100,
        },
        tooltip: 'Additional notes or comments for this appointment',
      },

      // Suggested & Previous Offerings (for scheduled appointments)
      {
        name: 'offerings_insights',
        type: 'custom' as const,
        layout: { span: 24 },
        render: (value: any, fieldRecord: any, form: any) => {
          if (!form || typeof form.getFieldValue !== 'function') {
            return null;
          }

          const status = form.getFieldValue('status');
          // Use record from hook (parent passes selectedRecord) or from form render (fieldRecord)
          const appointmentId = record?.id ?? fieldRecord?.id;

          // Show only when appointment exists and status is SCHEDULED
          const enabled = status === 'SCHEDULED' && !!appointmentId;
          if (!enabled) return null;

          const { renderDetails } = useOfferingsInsights({
            appointmentId,
            enabled,
          });

          return renderDetails();
        },
        dependencies: ['status'],
        required: false,
      },

      // Hidden field for database requirements
      {
        name: 'branch_slug',
        label: '',
        type: 'text' as const,
        required: false,
        defaultValue: 'Main',
        layout: { span: 0 },
        props: {
          style: { display: 'none' },
        },
        hidden: true,
      },
    ],
    [userOptionRender, patientOptionRender, patient, record]
  );
};
